import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import { useSelector } from 'react-redux';

import { RootState, useAppDispatch } from '../../../support/store';
import {
  fetchJourneyMaps,
  fetchJourneyMapReferenceData,
} from '../../../support/slices/journeyMapsSlice';
import {
  useEnsureJourneyMapsLoaded,
  useEnsureJourneyMapReferenceDataLoaded,
  useJourneyMaps,
  useJourneyMapTypes,
} from '../../../hooks/journey-map-hooks';
import { JourneyMap } from '../../../types/journeyMap';
import { deleteItem } from '../../../support/queries';

interface JourneyMapAdminListPageProps {
  onCreateNew?: () => void;
  onEditJourneyMap?: (journeyMapId: number) => void;
}

function JourneyMapAdminListPage({
                                   onCreateNew,
                                   onEditJourneyMap,
                                 }: JourneyMapAdminListPageProps) {
  const dispatch = useAppDispatch();
  const journeyMaps = useJourneyMaps();
  const journeyMapTypes = useJourneyMapTypes();
  const journeyMapsState = useSelector((state: RootState) => state.journeyMaps);

  useEnsureJourneyMapsLoaded();
  useEnsureJourneyMapReferenceDataLoaded();

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [deleteError, setDeleteError] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const filteredRows = useMemo(() => {
    return journeyMaps.filter((map) => {
      const searchValue = search.trim().toLowerCase();

      const matchesSearch =
        !searchValue ||
        map.name?.toLowerCase().includes(searchValue) ||
        map.description?.toLowerCase().includes(searchValue) ||
        map.slug?.toLowerCase().includes(searchValue);

      const matchesType =
        typeFilter === 'all' || String(map.journey_type || '') === typeFilter;

      const matchesStatus =
        statusFilter === 'all' || map.publication_status === statusFilter;

      const matchesActive =
        activeFilter === 'all' ||
        (activeFilter === 'active' && !!map.is_active) ||
        (activeFilter === 'inactive' && !map.is_active);

      return matchesSearch && matchesType && matchesStatus && matchesActive;
    });
  }, [journeyMaps, search, typeFilter, statusFilter, activeFilter]);

  const handleRefresh = async () => {
    await dispatch(fetchJourneyMaps() as any);
    await dispatch(fetchJourneyMapReferenceData() as any);
  };

  const handleDeleteJourneyMap = async (journeyMap: JourneyMap) => {
    if (!journeyMap.id) return;

    const confirmed = window.confirm(
      `Delete "${journeyMap.name}" and all related items, links, path groups, assignments, and progress records?`,
    );

    if (!confirmed) return;

    try {
      setDeleteError('');
      setDeletingId(journeyMap.id);

      await deleteItem(`db/journey-map/${journeyMap.id}/`);

      await dispatch(fetchJourneyMaps() as any);
      await dispatch(fetchJourneyMapReferenceData() as any);
    } catch (e: any) {
      setDeleteError(
        e?.message || 'Failed to delete Journey Map. Please try again.',
      );
    } finally {
      setDeletingId(null);
    }
  };

  const columns: GridColDef<JourneyMap>[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.2,
      minWidth: 220,
    },
    {
      field: 'journey_type',
      headerName: 'Type',
      minWidth: 180,
      renderCell: (params: GridRenderCellParams<JourneyMap>) => {
        const type = journeyMapTypes.find((t) => t.id === params.row.journey_type);
        return <span>{type?.name || '—'}</span>;
      },
    },
    {
      field: 'publication_status',
      headerName: 'Status',
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<JourneyMap>) => {
        const value = params.row.publication_status || 'draft';
        const color =
          value === 'published'
            ? 'success'
            : value === 'archived'
              ? 'default'
              : 'warning';

        return <Chip label={value} color={color as any} size="small" variant="outlined" />;
      },
    },
    {
      field: 'is_active',
      headerName: 'Active',
      minWidth: 110,
      renderCell: (params: GridRenderCellParams<JourneyMap>) => (
        <Chip
          label={params.row.is_active ? 'Yes' : 'No'}
          color={params.row.is_active ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'version',
      headerName: 'Version',
      minWidth: 100,
    },
    {
      field: 'estimated_duration_minutes',
      headerName: 'Duration (min)',
      minWidth: 130,
      renderCell: (params: GridRenderCellParams<JourneyMap>) => (
        <span>{params.row.estimated_duration_minutes ?? '—'}</span>
      ),
    },
    {
      field: 'journey_map_items_count',
      headerName: 'Items',
      minWidth: 90,
      sortable: false,
      renderCell: (params: GridRenderCellParams<JourneyMap>) => (
        <span>{params.row.journey_map_items?.length ?? 0}</span>
      ),
    },
    {
      field: 'last_updated',
      headerName: 'Last Updated',
      minWidth: 180,
      renderCell: (params: GridRenderCellParams<JourneyMap>) => (
        <span>
          {params.row.last_updated
            ? new Date(params.row.last_updated).toLocaleString()
            : '—'}
        </span>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      minWidth: 240,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<JourneyMap>) => (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<EditIcon />}
            onClick={() => {
              if (params.row.id && onEditJourneyMap) {
                onEditJourneyMap(params.row.id);
              }
            }}
          >
            Edit
          </Button>

          <Button
            size="small"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            disabled={deletingId === params.row.id}
            onClick={() => handleDeleteJourneyMap(params.row)}
          >
            {deletingId === params.row.id ? 'Deleting...' : 'Delete'}
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ p: 2, height: '100%', width: '100%', backgroundColor: '#f7f9fc' }}>
      <Stack spacing={2} sx={{ height: '100%' }}>
        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <AccountTreeIcon color="primary" />
                  <Typography variant="h5" fontWeight={700}>
                    Journey Maps
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Manage learning journeys, branching paths, grouped completion logic, and authoring flows.
                </Typography>
              </Box>

              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={journeyMapsState.isLoading || journeyMapsState.isReferenceDataLoading}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => onCreateNew?.()}
                >
                  New Journey Map
                </Button>
              </Stack>
            </Stack>

            {journeyMapsState.error && (
              <Alert severity="error">{journeyMapsState.error}</Alert>
            )}

            {journeyMapsState.referenceDataError && (
              <Alert severity="error">{journeyMapsState.referenceDataError}</Alert>
            )}

            {deleteError && (
              <Alert severity="error">{deleteError}</Alert>
            )}

            <Stack direction="row" spacing={2} flexWrap="wrap">
              <TextField
                label="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                sx={{ minWidth: 260 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  {journeyMapTypes.map((type) => (
                    <MenuItem key={type.id} value={String(type.id)}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Active</InputLabel>
                <Select
                  label="Active"
                  value={activeFilter}
                  onChange={(e) => setActiveFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </Paper>

        <Paper sx={{ flex: 1, minHeight: 0 }}>
          <Box sx={{ height: '100%', width: '100%' }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              loading={journeyMapsState.isLoading}
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 25,
                    page: 0,
                  },
                },
                sorting: {
                  sortModel: [{ field: 'last_updated', sort: 'desc' }],
                },
              }}
              sx={{
                border: 0,
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f5f7fa',
                },
              }}
            />
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
}

export default JourneyMapAdminListPage;